import { apiFetch } from '@/lib/api'
import type { CursorPage, ResourceField, ResourceRow } from '@/lib/resources'

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger'

export interface SheetActionField {
  name: string
  label: string
  kind: 'text' | 'textarea' | 'number' | 'boolean' | 'select' | 'fk'
  required: boolean
  hint: string
  choices: Array<{ value: string | number; label: string }>
  resource: string | null
  initial: unknown
}

export interface SheetAction {
  id: string
  label: string
  icon: string
  danger: boolean
  confirm: string
  tab: string | null
  fields: SheetActionField[]
}

export interface SheetTab {
  id: string
  label: string
  resource: string
  count: number
  single: boolean
}

export interface SheetHistoryEntry {
  id: number
  action: string
  actor: string
  created_at: string
  metadata: Record<string, unknown>
}

export interface SheetPhoto {
  url: string
  video: boolean
}

export interface SheetFact {
  label: string
  value: string
  href?: string
  record?: { key: string; id: string | number }
}

export type SheetSection =
  | { title: string; kind: 'text'; text: string }
  | { title: string; kind: 'facts'; items: SheetFact[]; empty?: string | null }
  | {
      title: string
      kind: 'toggles'
      items: Array<{ label: string; on: boolean }>
    }
  | { title: string; kind: 'tags'; items: string[] }
  | { title: string; kind: 'photos'; items: SheetPhoto[] }

export interface RelatedItem {
  id: string | number
  title: string
  meta: string[]
  date: string
  image: SheetPhoto | null
}

export interface RecordSheetData {
  key: string
  id: string | number
  title: string
  subtitle: string
  avatar: SheetPhoto | null
  cover: SheetPhoto | null
  badges: Array<{ label: string; tone: BadgeTone }>
  stats: Array<{ label: string; value: string | number; hint?: string }>
  sections: SheetSection[]
  /** Only used to prefill the Edit form. */
  record: ResourceRow
  tabs: SheetTab[]
  actions: SheetAction[]
  history: SheetHistoryEntry[]
}

const base = (key: string, id: string | number) =>
  `/api/admin/r/${encodeURIComponent(key)}/${encodeURIComponent(String(id))}`

export function fetchRecordSheet(
  key: string,
  id: string | number
): Promise<RecordSheetData> {
  return apiFetch<RecordSheetData>(`${base(key, id)}/sheet/`)
}

export function fetchRecordTab(
  key: string,
  id: string | number,
  tab: string,
  cursor?: string,
  pageSize = 5
): Promise<CursorPage<RelatedItem> & { resource: string }> {
  const query = new URLSearchParams({ page_size: String(pageSize) })
  if (cursor) query.set('cursor', cursor)
  return apiFetch(`${base(key, id)}/tabs/${encodeURIComponent(tab)}/?${query}`)
}

export function runRecordAction(
  key: string,
  id: string | number,
  action: string,
  values: Record<string, unknown>
): Promise<{ ok: boolean; message: string }> {
  return apiFetch(`${base(key, id)}/actions/${encodeURIComponent(action)}/`, {
    method: 'POST',
    body: values
  })
}

/** Map an action input onto the CRUD field shape so ResourceFieldEditor can render it. */
export function actionFieldAsResourceField(
  field: SheetActionField
): ResourceField {
  const widget = {
    text: 'text',
    textarea: 'textarea',
    number: 'number',
    boolean: 'switch',
    select: 'text',
    fk: 'fk'
  }[field.kind]
  return {
    name: field.name,
    type: field.kind,
    widget,
    label: field.label,
    hint: field.hint,
    required: field.required,
    readonly: false,
    redact: false,
    choices: field.kind === 'select' ? field.choices : undefined,
    fk_resource: field.resource,
    fk_model: field.resource?.split('__')[1]
  }
}

export interface PageStat {
  label: string
  value: string | number
  hint?: string
}

export function fetchResourceStats(
  key: string
): Promise<{ stats: PageStat[] }> {
  return apiFetch(`/api/admin/r/${encodeURIComponent(key)}/stats/`)
}
